import type { InvoiceListItemDTO } from "@ai-crm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Pagination } from "../../components/pagination";
import { adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import {
  formatInvoicePaymentDeadline,
  formatMoscowDateTime,
  formatPaymentRub,
} from "../../lib/format";
import {
  invoiceStatusLabels,
  paymentButton,
  paymentKeys,
  usePaymentScope,
  useVisiblePage,
} from "../../lib/payment-ui";
import { PaymentInvoicePanel } from "./payment-invoice-panel";

export function PaymentsTab({ accountId }: { accountId: string }) {
  const { token, admin } = useAdminAuth();
  return (
    <PaymentsWorkspace
      key={`${admin?.id}:${token}:${accountId}`}
      accountId={accountId}
    />
  );
}
function PaymentsWorkspace({ accountId }: { accountId: string }) {
  const scope = usePaymentScope(accountId),
    client = useQueryClient(),
    visible = useVisiblePage();
  const { capture, adminId } = scope;
  const [search, setSearch] = useSearchParams();
  const invoiceId = search.get("invoice") ?? "";
  const [numberDraft, setNumberDraft] = useState("");
  const [number, setNumber] = useState("");
  const [status, setStatus] = useState<InvoiceListItemDTO["status"] | "">("");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.at(-1);
  const history = useQuery({
    queryKey: [
      ...paymentKeys.account(scope.adminId, accountId),
      "history",
      number,
      status,
      cursor ?? "",
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.accountPayments(
          scope.token!,
          accountId,
          {
            number: number || undefined,
            status: status || undefined,
            cursor,
            limit: 10,
          },
          signal,
        ),
      ),
    enabled: Boolean(scope.token),
    refetchOnWindowFocus: "always",
  });
  const detail = useQuery({
    queryKey: [
      ...paymentKeys.account(scope.adminId, accountId),
      "invoice",
      invoiceId,
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.invoice(scope.token!, accountId, invoiceId, signal),
      ),
    enabled: Boolean(scope.token && invoiceId),
    refetchOnWindowFocus: "always",
    refetchInterval: (q) =>
      visible && q.state.data?.document.state === "WRITING" ? 3000 : false,
  });
  const onChanged = useCallback(
    async (replacementId?: string) => {
      const guard = capture();
      if (!guard.isCurrent()) return;
      setCursorStack([]);
      if (replacementId) {
        const next = new URLSearchParams();
        next.set("invoice", replacementId);
        next.set("previous", invoiceId);
        setSearch(next);
      }
      await Promise.allSettled([
        client.invalidateQueries({
          queryKey: paymentKeys.account(adminId, accountId),
        }),
        client.invalidateQueries({ queryKey: ["admin", "account", accountId] }),
        client.invalidateQueries({ queryKey: ["admin", "accounts"] }),
      ]);
    },
    [capture, adminId, accountId, client, invoiceId, setSearch],
  );
  function clearSelection() {
    const next = new URLSearchParams(search);
    next.delete("invoice");
    next.delete("previous");
    setSearch(next);
  }
  function refresh() {
    void history.refetch();
    if (invoiceId) void detail.refetch();
  }
  return (
    <div className="space-y-4">
      <section className="panel space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Платежи</h2>
          <button
            className={paymentButton}
            disabled={history.isFetching || detail.isFetching}
            onClick={refresh}
          >
            Обновить платежи
          </button>
        </div>
        {history.data && (
          <section
            className="space-y-3 rounded-md border p-3 text-sm"
            aria-label="Текущий доступ"
          >
            <h3 className="font-semibold">
              {history.data.billing.billing.tariff.name}
            </h3>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt>Доступно диалогов</dt>
                <dd>{history.data.billing.billing.availableDialogs}</dd>
              </div>
              <div>
                <dt>{history.data.billing.billing.quotaWindow === "TRIAL_TOTAL" ? "Использовано за пробный период" : "Использовано в текущем периоде"}</dt>
                <dd>{history.data.billing.billing.usedDialogs}</dd>
              </div>
              <div>
                <dt>{history.data.billing.billing.quotaWindow === "TRIAL_TOTAL" ? "Общий запас на пробный период" : "Месячный объём"}</dt>
                <dd>{history.data.billing.billing.monthlyDialogs}</dd>
              </div>
              <div>
                <dt>Зарезервировано</dt>
                <dd>{history.data.billing.billing.reservedDialogs}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt>Оплачено до</dt>
                <dd>
                  {history.data.billing.paidThroughAt
                    ? `${formatMoscowDateTime(history.data.billing.paidThroughAt)} МСК`
                    : "Нет оплаченного срока"}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt>{history.data.billing.billing.subscription.kind === "TRIAL" ? "Пробный период действует до" : "Конец текущего сегмента"}</dt>
                <dd>
                  {history.data.billing.billing.subscription.kind === "TRIAL"
                    ? `${formatMoscowDateTime(history.data.billing.billing.subscription.endsAt)} МСК`
                    : history.data.billing.segmentEndsAt
                      ? `${formatMoscowDateTime(history.data.billing.segmentEndsAt)} МСК`
                      : "Нет платного сегмента"}
                </dd>
              </div>
            </dl>
            {history.data.billing.futureSegments.length > 0 && (
              <details>
                <summary className="cursor-pointer font-medium">
                  Назначенные продления (
                  {history.data.billing.futureSegments.length})
                </summary>
                <div className="mt-2 space-y-2">
                  {history.data.billing.futureSegments.map((segment) => (
                    <div key={segment.id} className="rounded-md border p-3">
                      <p>
                        {segment.effectiveSnapshot.tariffName}:{" "}
                        {segment.effectiveSnapshot.monthlyDialogs} диалогов / 30
                        дней.
                      </p>
                      <p>
                        {formatMoscowDateTime(segment.startsAt)} —{" "}
                        {formatMoscowDateTime(segment.endsAt)} МСК.
                      </p>
                      <p>
                        Базовая цена диалога:{" "}
                        {segment.effectiveSnapshot.unitPriceRub} ₽.
                      </p>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </section>
        )}
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            setNumber(numberDraft.trim());
            setCursorStack([]);
            clearSelection();
          }}
        >
          <label className="min-w-0 flex-1 space-y-1 text-sm">
            <span>Номер счёта</span>
            <input
              className="control"
              value={numberDraft}
              pattern="[0-9-]*"
              onChange={(e) => setNumberDraft(e.target.value)}
            />
          </label>
          <label className="min-w-0 flex-1 space-y-1 text-sm">
            <span>Статус счёта</span>
            <select
              className="control"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setCursorStack([]);
                clearSelection();
              }}
            >
              <option value="">Все статусы</option>
              {Object.entries(invoiceStatusLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button className={paymentButton} type="submit">
            Найти
          </button>
        </form>
        {history.isPending && <p role="status">Загружаем платежи…</p>}
        {history.isError && (
          <p role="alert">
            Не удалось загрузить платежи.{" "}
            <button
              className={paymentButton}
              onClick={() => void history.refetch()}
            >
              Повторить
            </button>
          </p>
        )}
        {history.data?.items.length === 0 && (
          <p>
            {number || status
              ? "Нет счетов с такими условиями."
              : "Счета ещё не сформированы."}
          </p>
        )}
        {history.data && (
          <div className="space-y-2" aria-busy={history.isFetching}>
            {history.data.items.map((invoice) => {
              const needsPaymentConfirmation = invoice.status === "PAYMENT_REPORTED";
              return (
              <Link
                key={invoice.id}
                className={`block rounded-md p-3 text-sm hover:bg-muted/50 ${needsPaymentConfirmation ? "border-2 border-primary" : "border"} ${invoice.id === invoiceId ? "bg-muted/50" : ""}`}
                aria-current={invoice.id === invoiceId ? "page" : undefined}
                to={`/accounts/${accountId}/payments?invoice=${encodeURIComponent(invoice.id)}`}
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-semibold">№ {invoice.number}</span>
                  <span className="break-all tabular-nums">
                    {formatPaymentRub(invoice.totalRub)}
                  </span>
                </div>
                <p className="break-words">{invoice.description}</p>
                <p>
                  {formatMoscowDateTime(invoice.issuedAt)} МСК ·{" "}
                  {invoiceStatusLabels[invoice.status]}
                </p>
                {invoice.paymentReportedAt && (
                  <p>Клиент заявил об оплате: {formatMoscowDateTime(invoice.paymentReportedAt)} МСК.</p>
                )}
                <p>Оплатить {formatInvoicePaymentDeadline(invoice.expiresAt)}.</p>
                <p>
                  PDF:{" "}
                  {invoice.document.state === "READY"
                    ? "готов"
                    : invoice.document.state === "WRITING"
                      ? "формируется"
                      : "ошибка"}
                </p>
              </Link>
              );
            })}
          </div>
        )}
        <Pagination
          canGoBack={cursorStack.length > 0}
          canGoForward={Boolean(
            history.data?.nextCursor &&
            !cursorStack.includes(history.data.nextCursor),
          )}
          isLoading={history.isFetching}
          onBack={() => setCursorStack((s) => s.slice(0, -1))}
          onForward={() => {
            const next = history.data?.nextCursor;
            if (next && !cursorStack.includes(next))
              setCursorStack((s) => [...s, next]);
          }}
        />
      </section>
      {invoiceId && (
        <section className="panel min-w-0 space-y-3 p-4">
          {search.get("previous") && (
            <Link
              className="text-sm text-primary underline"
              to={`/accounts/${accountId}/payments?invoice=${encodeURIComponent(search.get("previous")!)}`}
            >
              Исходный счёт
            </Link>
          )}
          {detail.isPending && <p role="status">Загружаем счёт…</p>}
          {detail.isError && (
            <p role="alert">
              Счёт недоступен.{" "}
              <button
                className={paymentButton}
                onClick={() => void detail.refetch()}
              >
                Повторить
              </button>
            </p>
          )}
          {detail.data && (
            <PaymentInvoicePanel
              key={detail.data.id}
              accountId={accountId}
              invoice={detail.data}
              onChanged={onChanged}
            />
          )}
        </section>
      )}
    </div>
  );
}
